export class Random {
  constructor(
    private w = 88675123,
    private x = 56782657,
    private y = 237891545,
    private z = 448547851,
  ) {}

  next() {
    let t

    t = this.x ^ (this.x << 11)
    this.x = this.y
    this.y = this.z
    this.z = this.w
    return (this.w = this.w ^ (this.w >>> 19) ^ (t ^ (t >>> 8)))
  }

  /**
   * min以上max以下の整数疑似乱数を返す
   */
  nextInt(min, max) {
    const r = Math.abs(this.next())
    return min + (r % (max + 1 - min))
  }
}

export const random = new Random(new Date().getTime())

export function shuffle(array: any[]) {
  for (let i = array.length; 1 < i; i--) {
    const k = random.nextInt(0, i - 1)
    ;[array[k], array[i - 1]] = [array[i - 1], array[k]]
  }
}

export function equals(o1: any, o2: any) {
  return JSON.stringify(o1) === JSON.stringify(o2)
}

export function equalsIgnoreNull(
  o1: any,
  o2: any,
  keys: string[] = [],
): boolean {
  if (o1 === null || o2 === null) {
    return false
  }
  if (o1 instanceof Date && o2 instanceof Date) {
    return o1.toDateString() === o2.toDateString()
  } else if (isEmpty(o1) && isEmpty(o2)) {
    return true
  } else if (typeof o1 === 'object' && typeof o2 === 'object') {
    if (keys.length === 0) {
      keys = uniqueArray([...Object.keys(o1), ...Object.keys(o2)])
    }
    return keys.every(k => {
      if (o1[k] && o2[k]) {
        return equalsIgnoreNull(o1[k], o2[k])
      } else if (!o1[k] && !o2[k]) {
        return true
      } else if (!o1[k] && isEmpty(o2[k])) {
        return true
      } else if (isEmpty(o1[k]) && !o2[k]) {
        return true
      } else {
        return false
      }
    })
  } else if (Array.isArray(o1) && Array.isArray(o2)) {
    const len = Math.max(o1.length, o2.length)
    for (let i = 0; i < len; i++) {
      if (!equalsIgnoreNull(o1[i], o2[i])) {
        return false
      }
    }
    return true
  } else {
    return o1 === o2
  }
}

export function isEmpty(v: any): boolean {
  if (v === undefined) {
    return true
  }
  if (v == null) {
    return true
  }
  if (v === '') {
    return true
  }
  if (v instanceof Date) {
    return false
  }
  if (typeof v === 'object') {
    const keys = Object.keys(v)
    if (keys.length === 0) {
      return true
    }
    if (keys.map(k => v[k]).every(ov => isEmpty(ov))) {
      return true
    }
  } else if (Array.isArray(v)) {
    if (v.length === 0) {
      return true
    }
    if (v.every(av => isEmpty(av))) {
      return true
    }
  }
  return false
}

export function isEmptyString(v: string): boolean {
  return !v || /^[\s\0]+$/.test(v)
}

export function normalizeEmpty(v: any) {
  if (isEmpty(v)) {
    return null
  }
  if (Array.isArray(v)) {
    const a = []
    v.forEach(av => {
      const normalizedValue = normalizeEmpty(av)
      if (!isEmpty(normalizedValue)) {
        a.push(normalizedValue)
      }
    })
    return a
  } else if (typeof v === 'object') {
    const o = {}
    Object.keys(v).forEach(k => {
      const normalizedValue = normalizeEmpty(v[k])
      if (!isEmpty(normalizedValue)) {
        o[k] = normalizedValue
      }
    })
    return o
  }
  return v
}

export function removeNulls(v: any) {
  if (Array.isArray(v)) {
    const a = []
    v.forEach(av => {
      a.push(removeNulls(av))
    })
    return a
  } else if (typeof v === 'object') {
    const o = {}
    Object.keys(v).forEach(k => {
      if (v[k] != null) {
        o[k] = removeNulls(v[k])
      }
    })
    return o
  }
  return v
}

export function deepFind(obj: any, path: string, def?: any): any {
  for (const p of path.split('.')) {
    if (obj[p]) {
      obj = obj[p]
    } else {
      return def
    }
  }
  return obj
}
export function deepFind2(obj: any, path: string, def: any = []): any[] {
  const values = []
  _deepFind2(path.split('.'), obj, values)
  if (values.length === 0) {
    return def
  }
  return values
}

export function deepFind3(obj: any, paths: string[]): any[] {
  const values = []
  if (paths) {
    paths.forEach(path => {
      _deepFind2(path.split('.'), obj, values)
    })
  }
  return values
}

function _deepFind2(paths: string[], obj: any, values: any[]) {
  if (obj == null) {
    return
  }
  const sub = obj[paths[0]]
  if (sub !== null && sub !== undefined) {
    if (paths.length === 1) {
      if (Array.isArray(sub)) {
        ;(<any[]>sub).forEach(v => {
          values.push(v)
        })
      } else {
        values.push(sub)
      }
    } else if (Array.isArray(sub)) {
      ;(<any[]>sub).forEach(node =>
        _deepFind2(paths.slice(1, paths.length), node, values),
      )
    } else {
      _deepFind2(paths.slice(1, paths.length), sub, values)
    }
  }
}

export function clone<T>(tgt: T): T {
  let cp: any
  if (tgt === null) {
    cp = null
  } else if (tgt instanceof Date) {
    cp = new Date(tgt.getTime())
  } else if (Array.isArray(tgt)) {
    cp = []
    tgt.forEach((v, i, arr) => {
      cp.push(v)
    })
    cp = cp.map((n: any) => clone(n))
  } else if (typeof tgt === 'object') {
    cp = { ...(tgt as Record<string, unknown>) }
    Object.keys(cp).forEach(k => {
      cp[k] = clone(cp[k])
    })
  } else {
    cp = tgt
  }
  return cp
}

export function cloneExcept(object: any, ...except: string[]): any {
  const cp: any = {}
  Object.keys(object)
    .filter(key => !except.includes(key))
    .forEach(key => {
      cp[key] = clone(object[key])
    })
  return cp
}

export function cloneJson<T>(tgt: T): T {
  return JSON.parse(JSON.stringify(tgt))
}

export function merge(from: any, to: any) {
  if (typeof from === 'object' && typeof to === 'object') {
    Object.keys(from).forEach(k => {
      const fromval = from[k]
      if (fromval === null) {
        // do nothing
      } else if (fromval instanceof Date) {
        to[k] = clone(fromval)
      } else if (Array.isArray(fromval)) {
        to[k] = clone(fromval)
      } else if (typeof fromval === 'object') {
        let toval = to[k]
        if (toval !== null && typeof toval === 'object') {
          // toもfromもobjectの場合、マージ
          toval = clone(toval)
          merge(fromval, toval)
          to[k] = toval
        } else {
          // toがobjectでない場合はcloneで上書き
          to[k] = clone(fromval)
        }
      } else {
        to[k] = fromval
      }
    })
  }
}

export function deepMerge(target, ...sources) {
  if (sources.length == 0) return target

  const targetIsArray = Array.isArray(target)
  // 配列だったら [] オブジェクトだったら {} にする
  const result = Object.assign(targetIsArray ? [] : {}, target)

  sources.forEach((source) => {
    for (const key in source) {
      const targetValue = result[key]
      const sourceValue = source[key]

      // objectだったら再起させる
      if (typeof targetValue === 'object' && typeof sourceValue === 'object') {
        result[key] = deepMerge(targetValue, sourceValue)
        continue
      }

      // 配列だったら重複してなければ追加する
      if (targetIsArray) {
        if (!result.includes(sourceValue)) {
          result.push(sourceValue)
        }
        continue
      }

      // 配列以外は値を上書きしていく
      result[key] = sourceValue
    }
  })

  return result
}

export function ensure(object: any, field: string, value: any) {
  if (object[field] == null) object[field] = value
}

export function deleteProperties(
  object: any,
  criteria: (k: string, v: any) => boolean,
): void {
  if (object === null) {
    return
  }
  if (Array.isArray(object)) {
    ;(<any[]>object).forEach(v => {
      deleteProperties(v, criteria)
    })
  } else if (typeof object === 'object') {
    Object.keys(object).forEach(k => {
      if (criteria(k, object[k])) {
        delete object[k]
      } else {
        deleteProperties(object[k], criteria)
      }
    })
  }
}

export function deepFreeze(obj: any) {
  if (!obj) {
    return undefined
  }
  Object.keys(obj)?.forEach(prop => {
    if (typeof obj[prop] === 'object' && !Object.isFrozen(obj[prop])) {
      deepFreeze(obj[prop])
    }
  })
  return Object.freeze(obj)
}

export function isEmptyArray(array: any[]) {
  return !array || array.length === 0
}

export function removeFromArray(array: any[], toRemove: any) {
  const index = array.indexOf(toRemove)
  if (index >= 0) {
    array.splice(index, 1)
  }
}

export function getFirstFromArray(array: any[]) {
  if (!array || array.length === 0) {
    return null
  }
  return array[0]
}

export function updateToArray<T>(array: T[], oldValue: T, newValue: T) {
  const index = array.indexOf(oldValue)
  if (index >= 0) {
    array[index] = newValue
  }
}

export function flatArray(array: any) {
  return array.reduce((acc, val) => acc.concat(val), [])
}

export function uniqueArray(array: any) {
  return array.filter((v, i) => array.indexOf(v) === i)
}

export function unqueNonEmptyArray(list: string[]) {
  if (list) {
    return uniqueArray(list.filter(c => !isEmptyString(c)))
  } else {
    return []
  }
}

export function listToJoinString(val: any[] | any) {
  let ret = []
  if (Array.isArray(val)) {
    ret = val
  } else {
    if (val) ret.push(val)
  }
  if (ret.length > 0) return uniqueArray(ret).join(', ')
  return ''
}

export function jsonReplacer(key: string, value: any) {
  if (key === '') {
    return value
  } else if (!value) {
    // 空の値の場合は対象に含めない
    return undefined
  } else if (value === '') {
    // 空文字は対象に含めない
    return undefined
  } else if (Array.isArray(value) && value.length === 0) {
    // 空配列は対象に含めない
    return undefined
  } else if (typeof value === 'object') {
    const keys = Object.keys(value)
    if (keys.length === 0) {
      // 空オブジェクトは対象に含めない
      return undefined
    } else if (keys.length === 2 && keys.indexOf('ja') !== -1
      && keys.indexOf('en') !== -1) {
      // 空の日本語・英語のオブジェクトも対象に含めない
      if (value.ja === '' && value.en === '') {
        return undefined
      }
    } else if (keys.length === 3 && keys.indexOf('ja') !== -1
      && keys.indexOf('en') !== -1 && keys.indexOf('yomi') !== -1) {
      // 空の日本語・英語・よみのオブジェクトも対象に含めない
      if (value.ja === '' && value.en === '' && value.yomi === '') {
        return undefined
      }
    }
    return value
  } else {
    return value
  }
}
